import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import type {
  ChannelRecord,
  CreateUpstreamAccountRequest,
  CreateUpstreamKeyBucketRequest,
  UpstreamAccountRecord,
  UpstreamKeyBucketRecord,
} from '@nadoutong/contracts';
import {
  useUpstreamAccountsList,
  useCreateUpstreamAccount,
  useUpdateUpstreamAccount,
  useDeleteUpstreamAccount,
} from '../api/useUpstreamAccounts.ts';
import { useKeyBucketsList, useCreateKeyBucket, useDeleteKeyBucket } from '../api/useKeyBuckets.ts';
import { useChannelsList } from '../api/useChannels.ts';
import { setBindings } from '../api/keyChannelBindings.ts';
import { useKeyChannelBindingsList, useSetKeyChannelBindings } from '../api/useKeyChannelBindings.ts';
import { StatusChip } from './StatusChip.tsx';
import { Modal } from './Modal.tsx';
import { useToast, coerceErrorMessage } from './Toast.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { INPUT_CLASS_ON_HIGHEST } from '../styles/forms.ts';

/**
 * "Accounts" tab content for UpstreamDetail.
 *
 * Lists upstream accounts as expandable cards. Each account shows its
 * label, enabled state, RPM limit and rate multiplier. When expanded,
 * key buckets belonging to the account are listed with their channel
 * binding chips.
 */
export function AccountsCard({ upstreamId }: { readonly upstreamId: string }): JSX.Element {
  const t = useT();
  const { data: accounts, isLoading, isError, error } = useUpstreamAccountsList(upstreamId);
  const [addOpen, setAddOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="rounded-xl bg-surface-container p-6">
        <div className="h-8 w-48 animate-pulse rounded bg-surface-container-high mb-4" />
        <div className="h-16 animate-pulse rounded bg-surface-container-high" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-xl bg-surface-container p-6" data-testid="accounts-card-error">
        <p className="text-sm text-error" role="alert">
          {error instanceof Error ? error.message : t('common.network_error')}
        </p>
      </div>
    );
  }

  const items = accounts ?? [];

  return (
    <div className="rounded-xl bg-surface-container" data-testid="accounts-card">
      <div className="flex items-center justify-between px-6 pt-5 pb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
            {t('upstream_accounts.title')}
          </h3>
          {items.length > 0 ? (
            <span className="text-[10px] text-outline">
              {t('upstream_accounts.count', { count: String(items.length) })}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          data-testid="accounts-add-btn"
          className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
        >
          {t('upstream_accounts.add_btn')}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="px-6 pb-6 text-sm text-outline" data-testid="accounts-empty">
          {t('upstream_accounts.empty')}
        </div>
      ) : (
        <ul className="flex flex-col gap-3 px-6 pb-6" data-testid="accounts-list">
          {items.map((acct) => (
            <AccountRow
              key={acct.id}
              account={acct}
              upstreamId={upstreamId}
              expanded={expandedId === acct.id}
              onToggleExpand={() =>
                setExpandedId(expandedId === acct.id ? null : acct.id)
              }
            />
          ))}
        </ul>
      )}

      {addOpen ? (
        <CreateAccountModal
          upstreamId={upstreamId}
          onClose={() => setAddOpen(false)}
        />
      ) : null}
    </div>
  );
}

// ─── Account row (expandable) ──────────────────────────────────────────

interface AccountRowProps {
  readonly account: UpstreamAccountRecord;
  readonly upstreamId: string;
  readonly expanded: boolean;
  readonly onToggleExpand: () => void;
}

function AccountRow({ account, upstreamId, expanded, onToggleExpand }: AccountRowProps): JSX.Element {
  const t = useT();
  const updateMut = useUpdateUpstreamAccount(upstreamId);
  const deleteMut = useDeleteUpstreamAccount(upstreamId);
  const toast = useToast();
  const [confirmDelete, setConfirmDelete] = useState(false);

  function handleToggleEnabled(): void {
    updateMut.mutate(
      { accountId: account.id, body: { enabled: !account.enabled } },
      {
        onError: (err) => toast.error(coerceErrorMessage(err)),
      },
    );
  }

  function handleDelete(): void {
    deleteMut.mutate(account.id, {
      onSuccess: () => {
        toast.success(t('upstream_accounts.toast.deleted'));
        setConfirmDelete(false);
      },
      onError: (err) => toast.error(coerceErrorMessage(err)),
    });
  }

  return (
    <li
      className="flex flex-col rounded-lg bg-surface-container-low"
      data-testid={`account-row-${account.id}`}
    >
      {/* Header */}
      <button
        type="button"
        onClick={onToggleExpand}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-bright/30"
        aria-expanded={expanded}
        data-testid={`account-toggle-${account.id}`}
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-on-surface">{account.label}</span>
          <StatusChip
            variant={account.enabled ? 'ok' : 'neutral'}
            label={account.enabled ? t('upstream_accounts.chip.enabled') : t('upstream_accounts.chip.disabled')}
          />
          <span className="rounded-full bg-surface-container-highest px-2 py-0.5 text-[10px] font-medium text-on-surface-variant">
            RPM: {account.rpm_limit}
          </span>
          <span className="rounded-full bg-surface-container-highest px-2 py-0.5 text-[10px] font-medium text-on-surface-variant">
            {t('upstream_accounts.rate_multiplier_short')}: {account.rate_multiplier}
          </span>
        </div>
        <svg
          className={`h-4 w-4 shrink-0 text-outline transition-transform ${expanded ? 'rotate-180' : ''}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* Expanded content */}
      {expanded ? (
        <div className="flex flex-col gap-4 border-t border-outline-variant/10 px-4 py-4">
          {/* Inline editing row */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <InlineEditField
              label="RPM"
              initialValue={String(account.rpm_limit)}
              type="number"
              onSubmit={(v) => {
                const rpm = parseInt(v, 10);
                if (Number.isFinite(rpm) && rpm > 0) {
                  updateMut.mutate({ accountId: account.id, body: { rpm_limit: rpm } });
                }
              }}
            />
            <InlineEditField
              label={t('upstream_accounts.add_modal.rate_multiplier')}
              initialValue={String(account.rate_multiplier)}
              type="number"
              step="0.01"
              onSubmit={(v) => {
                const mul = parseFloat(v);
                if (Number.isFinite(mul) && mul > 0) {
                  updateMut.mutate({ accountId: account.id, body: { rate_multiplier: mul } });
                }
              }}
            />
            <div className="flex flex-col gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
                {t('upstream_accounts.chip.status_label')}
              </span>
              <button
                type="button"
                onClick={handleToggleEnabled}
                disabled={updateMut.isPending}
                className={`inline-flex w-fit items-center rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] transition-colors ${
                  account.enabled
                    ? 'bg-primary/10 text-primary hover:bg-primary/20'
                    : 'bg-outline-variant/20 text-outline hover:bg-outline-variant/30'
                } disabled:opacity-40`}
              >
                {account.enabled ? t('upstream_accounts.chip.enabled') : t('upstream_accounts.chip.disabled')}
              </button>
            </div>
          </div>

          {/* Key buckets for this account */}
          <div className="flex flex-col gap-2">
            <h4 className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('upstream_accounts.section.keys')}
            </h4>
            <AccountKeyBucketsList
              upstreamId={upstreamId}
              accountId={account.id}
            />
          </div>

          {/* Delete action */}
          <div className="flex items-center justify-end gap-2 border-t border-outline-variant/10 pt-3">
            {confirmDelete ? (
              <>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  className="text-xs font-medium text-outline hover:text-on-surface"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleteMut.isPending}
                  className="text-xs font-bold text-error hover:text-error/80 disabled:opacity-40"
                >
                  {deleteMut.isPending ? t('common.deleting') : t('common.delete')}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="text-xs font-medium text-outline hover:text-error transition-colors"
                data-testid={`account-delete-${account.id}`}
              >
                {t('common.delete')}
              </button>
            )}
          </div>
        </div>
      ) : null}
    </li>
  );
}

// ─── Key buckets filtered by account ───────────────────────────────────

function AccountKeyBucketsList({
  upstreamId,
  accountId,
}: {
  readonly upstreamId: string;
  readonly accountId: string;
}): JSX.Element {
  const t = useT();
  const { data: allBuckets, isLoading } = useKeyBucketsList(upstreamId);
  const [addKeyOpen, setAddKeyOpen] = useState(false);

  if (isLoading) {
    return <span className="text-xs text-outline">{t('common.loading')}</span>;
  }

  const buckets = (allBuckets ?? []).filter((kb) => kb.account_id === accountId);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
          {buckets.length > 0 ? `${buckets.length} keys` : ''}
        </span>
        <button
          type="button"
          onClick={() => setAddKeyOpen(true)}
          className="inline-flex items-center justify-center rounded-lg bg-primary/10 px-3 py-1 text-[11px] font-bold text-primary transition-colors hover:bg-primary/20"
        >
          {t('upstream_accounts.keys.add_btn')}
        </button>
      </div>
      {buckets.length === 0 ? (
        <span className="text-xs text-outline">
          {t('upstream_accounts.keys.none')}
        </span>
      ) : (
        buckets.map((kb) => (
          <KeyBucketWithChannels
            key={kb.id}
            bucket={kb}
            upstreamId={upstreamId}
          />
        ))
      )}
      {addKeyOpen ? (
        <CreateKeyModal
          upstreamId={upstreamId}
          accountId={accountId}
          onClose={() => setAddKeyOpen(false)}
        />
      ) : null}
    </div>
  );
}

function KeyBucketWithChannels({
  bucket,
  upstreamId,
}: {
  readonly bucket: UpstreamKeyBucketRecord;
  readonly upstreamId: string;
}): JSX.Element {
  const t = useT();
  const bindingsQ = useKeyChannelBindingsList(upstreamId, bucket.id);
  const channelsQ = useChannelsList();
  const deleteMut = useDeleteKeyBucket(upstreamId);
  const toast = useToast();
  const bindings = bindingsQ.data ?? [];
  const channels = channelsQ.data ?? [];
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingChannels, setEditingChannels] = useState(false);

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-surface-container-high/30 px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-on-surface">
          {bucket.label || '—'}
        </span>
        <span className="font-mono text-[10px] text-outline">
          {bucket.auth_secret_preview ?? 'write-only'}
        </span>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${bucket.enabled ? 'bg-primary/10 text-primary' : 'bg-outline-variant/20 text-outline'}`}>
          {bucket.enabled ? t('key_buckets.enabled') : t('key_buckets.disabled')}
        </span>
        <div className="ml-auto flex items-center gap-1">
          {bindings.length > 0 ? (
            bindings.map((b) => {
              const ch = channels.find((c) => c.id === b.channel_id);
              return (
                <span
                  key={b.channel_id}
                  className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary"
                >
                  {ch !== undefined ? `${ch.display_name} · ${ch.vendor}` : b.channel_slug}
                </span>
              );
            })
          ) : (
            <span className="text-[10px] text-outline">
              {t('upstream_accounts.channels.none')}
            </span>
          )}
          <button
            type="button"
            onClick={() => setEditingChannels(true)}
            className="ml-1 rounded p-1 text-outline hover:text-primary hover:bg-primary/10 transition-colors"
            title={t('upstream_accounts.keys.edit_channels')}
          >
            <span className="material-symbols-outlined text-[14px]">edit</span>
          </button>
          {confirmDelete ? (
            <>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="text-[10px] font-medium text-outline hover:text-on-surface"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  deleteMut.mutate(bucket.id, {
                    onSuccess: () => toast.success(t('upstream_accounts.keys.toast.deleted')),
                    onError: (err) => toast.error(coerceErrorMessage(err)),
                  });
                }}
                disabled={deleteMut.isPending}
                className="text-[10px] font-bold text-error hover:text-error/80 disabled:opacity-40"
              >
                {t('common.delete')}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="rounded p-1 text-outline hover:text-error hover:bg-error/10 transition-colors"
            >
              <span className="material-symbols-outlined text-[14px]">delete</span>
            </button>
          )}
        </div>
      </div>
      {editingChannels ? (
        <KeyChannelEditor
          upstreamId={upstreamId}
          bucketId={bucket.id}
          currentBindings={bindings}
          onClose={() => setEditingChannels(false)}
        />
      ) : null}
    </div>
  );
}

// ─── Key channel editor (inline) ──────────────────────────────────────

function KeyChannelEditor({
  upstreamId,
  bucketId,
  currentBindings,
  onClose,
}: {
  readonly upstreamId: string;
  readonly bucketId: string;
  readonly currentBindings: readonly { channel_id: string; channel_slug: string }[];
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const channelsQ = useChannelsList();
  const setMut = useSetKeyChannelBindings(upstreamId, bucketId);
  const toast = useToast();
  const channels = channelsQ.data ?? [];
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(currentBindings.map((b) => b.channel_id)),
  );

  function toggleId(id: string): void {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function handleSave(): void {
    const bindings: Array<{ channel_id: string; channel_slug: string }> = [];
    for (const id of selectedIds) {
      const ch = channels.find((c) => c.id === id);
      if (ch !== undefined) bindings.push({ channel_id: ch.id, channel_slug: ch.slug });
    }
    setMut.mutate({ bindings }, {
      onSuccess: () => { toast.success(t('upstream_accounts.keys.toast.channels_updated')); onClose(); },
      onError: (err) => toast.error(coerceErrorMessage(err)),
    });
  }

  return (
    <div className="flex flex-col gap-2 border-t border-outline-variant/10 pt-2">
      <div className="flex flex-wrap gap-1">
        {channels.map((ch) => (
          <button
            key={ch.id}
            type="button"
            onClick={() => toggleId(ch.id)}
            disabled={setMut.isPending}
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors ${
              selectedIds.has(ch.id)
                ? 'bg-primary text-on-primary'
                : 'bg-surface-container-highest text-on-surface-variant hover:bg-surface-bright'
            } disabled:opacity-40`}
          >
            {ch.display_name} · {ch.vendor}
          </button>
        ))}
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} disabled={setMut.isPending} className="text-[10px] font-medium text-outline hover:text-on-surface">
          {t('common.cancel')}
        </button>
        <button type="button" onClick={handleSave} disabled={setMut.isPending} className="text-[10px] font-bold text-primary hover:text-primary/80 disabled:opacity-40">
          {t('common.save')}
        </button>
      </div>
    </div>
  );
}

// ─── Inline edit field ─────────────────────────────────────────────────

function InlineEditField({
  label,
  initialValue,
  type,
  step,
  onSubmit,
}: {
  readonly label: string;
  readonly initialValue: string;
  readonly type: 'text' | 'number';
  readonly step?: string;
  readonly onSubmit: (value: string) => void;
}): JSX.Element {
  const [value, setValue] = useState(initialValue);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setValue(initialValue);
    setDirty(false);
  }, [initialValue]);

  function commit(): void {
    if (!dirty) return;
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      setValue(initialValue);
      setDirty(false);
      return;
    }
    onSubmit(trimmed);
    setDirty(false);
  }

  return (
    <label className="flex flex-col gap-2">
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
        {label}
      </span>
      <input
        type={type}
        step={step}
        value={value}
        onChange={(e) => {
          setValue(e.currentTarget.value);
          setDirty(true);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
        className="w-full rounded-lg bg-surface-container-lowest px-3 py-2 text-sm text-on-surface font-mono border-0 border-b border-outline-variant focus:border-primary focus:outline-none focus:ring-0 transition-colors"
      />
    </label>
  );
}

// ─── Create key modal ─────────────────────────────────────────────────

function CreateKeyModal({
  upstreamId,
  accountId,
  onClose,
}: {
  readonly upstreamId: string;
  readonly accountId: string;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const createMut = useCreateKeyBucket(upstreamId);
  const channelsQ = useChannelsList();
  const { fetchImpl } = useSession();
  const toast = useToast();
  const [formError, setFormError] = useState<string | null>(null);
  const [selectedChannelIds, setSelectedChannelIds] = useState<Set<string>>(new Set());

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFormError(null);
    const fd = new FormData(e.currentTarget);
    const authSecretRef = (fd.get('auth_secret_ref') as string).trim();
    if (authSecretRef.length === 0) {
      setFormError(t('upstream_accounts.keys.add_modal.error.secret_required'));
      return;
    }

    const label = (fd.get('label') as string).trim();

    const body: CreateUpstreamKeyBucketRequest = {
      account_id: accountId,
      auth_secret_ref: authSecretRef,
      ...(label.length > 0 ? { label } : {}),
    };

    const bindingEntries: Array<{ channel_id: string; channel_slug: string }> = [];
    for (const id of selectedChannelIds) {
      const ch = channels.find((c) => c.id === id);
      if (ch !== undefined) bindingEntries.push({ channel_id: ch.id, channel_slug: ch.slug });
    }

    createMut.mutate(body, {
      onSuccess: async (res) => {
        if (bindingEntries.length > 0) {
          try {
            await setBindings(upstreamId, res.key_bucket.id, {
              bindings: bindingEntries,
            }, fetchImpl);
          } catch {
            toast.error(t('upstream_accounts.keys.toast.channels_failed'));
          }
        }
        toast.success(t('upstream_accounts.keys.toast.created'));
        onClose();
      },
      onError: (err) => {
        const msg = coerceErrorMessage(err);
        setFormError(msg);
        toast.error(msg);
      },
    });
  }

  const channels: readonly ChannelRecord[] = channelsQ.data ?? [];

  return (
    <Modal
      isOpen
      onClose={() => { if (!createMut.isPending) onClose(); }}
      titleId="key-create-title"
      testId="key-create-modal"
    >
      <form className="flex flex-col gap-6 p-8" onSubmit={handleSubmit} noValidate>
        <h2 id="key-create-title" className="text-base font-semibold text-on-surface">
          {t('upstream_accounts.keys.add_modal.title')}
        </h2>

        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('upstream_accounts.keys.add_modal.auth_secret_ref')}
          </span>
          <input
            name="auth_secret_ref"
            type="text"
            placeholder={t('upstream_accounts.keys.add_modal.auth_secret_ref.placeholder')}
            disabled={createMut.isPending}
            className={INPUT_CLASS_ON_HIGHEST}
            autoComplete="off"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('upstream_accounts.keys.add_modal.label')}
          </span>
          <input
            name="label"
            type="text"
            disabled={createMut.isPending}
            className={INPUT_CLASS_ON_HIGHEST}
            autoComplete="off"
          />
        </label>

        <div className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('upstream_accounts.keys.add_modal.channels')}
          </span>
          <span className="text-[11px] text-outline">
            {t('upstream_accounts.keys.add_modal.channels_hint')}
          </span>
          <div className="flex flex-col gap-1 rounded-lg bg-surface-container-lowest p-2 max-h-[160px] overflow-y-auto">
            {channels.map((ch) => (
              <label
                key={ch.id}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors cursor-pointer ${
                  selectedChannelIds.has(ch.id)
                    ? 'bg-primary/10 text-on-surface'
                    : 'text-on-surface-variant hover:bg-surface-bright/30'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedChannelIds.has(ch.id)}
                  onChange={() => {
                    setSelectedChannelIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(ch.id)) {
                        next.delete(ch.id);
                      } else {
                        next.add(ch.id);
                      }
                      return next;
                    });
                  }}
                  disabled={createMut.isPending}
                  className="h-4 w-4 shrink-0 accent-primary"
                />
                <span className="font-medium">{ch.display_name}</span>
                <span className="ml-auto text-[10px] text-outline">{ch.vendor}</span>
              </label>
            ))}
          </div>
        </div>

        {formError !== null ? (
          <p className="text-xs font-medium text-error" role="alert">
            {formError}
          </p>
        ) : null}

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => { if (!createMut.isPending) onClose(); }}
            disabled={createMut.isPending}
            className="inline-flex items-center justify-center rounded-lg bg-surface-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('upstream_accounts.add_modal.cancel')}
          </button>
          <button
            type="submit"
            disabled={createMut.isPending}
            className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] disabled:opacity-40 disabled:pointer-events-none"
          >
            {createMut.isPending
              ? t('upstream_accounts.keys.add_modal.creating')
              : t('upstream_accounts.keys.add_modal.submit')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Create account modal ──────────────────────────────────────────────

function CreateAccountModal({
  upstreamId,
  onClose,
}: {
  readonly upstreamId: string;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const createMut = useCreateUpstreamAccount(upstreamId);
  const toast = useToast();
  const [formError, setFormError] = useState<string | null>(null);

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFormError(null);
    const fd = new FormData(e.currentTarget);
    const label = (fd.get('label') as string).trim();
    if (label.length === 0) {
      setFormError(t('upstream_accounts.add_modal.error.label_required'));
      return;
    }

    const rpmStr = (fd.get('rpm_limit') as string).trim();
    const rateStr = (fd.get('rate_multiplier') as string).trim();
    const rpm = rpmStr.length > 0 ? parseInt(rpmStr, 10) : undefined;
    const rate = rateStr.length > 0 ? parseFloat(rateStr) : undefined;

    const body: CreateUpstreamAccountRequest = {
      label,
      ...(rpm !== undefined && Number.isFinite(rpm) ? { rpm_limit: rpm } : {}),
      ...(rate !== undefined && Number.isFinite(rate) ? { rate_multiplier: rate } : {}),
    };

    createMut.mutate(body, {
      onSuccess: () => {
        toast.success(t('upstream_accounts.toast.created'));
        onClose();
      },
      onError: (err) => {
        const msg = coerceErrorMessage(err);
        setFormError(msg);
        toast.error(msg);
      },
    });
  }

  return (
    <Modal
      isOpen
      onClose={() => { if (!createMut.isPending) onClose(); }}
      titleId="accounts-create-title"
      testId="accounts-create-modal"
    >
      <form className="flex flex-col gap-6 p-8" onSubmit={handleSubmit} noValidate>
        <h2 id="accounts-create-title" className="text-base font-semibold text-on-surface">
          {t('upstream_accounts.add_modal.title')}
        </h2>

        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('upstream_accounts.add_modal.label')}
          </span>
          <input
            name="label"
            type="text"
            placeholder={t('upstream_accounts.add_modal.label.placeholder')}
            disabled={createMut.isPending}
            className={INPUT_CLASS_ON_HIGHEST}
            autoComplete="off"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('upstream_accounts.add_modal.rpm')}
          </span>
          <input
            name="rpm_limit"
            type="number"
            min={1}
            defaultValue="60"
            disabled={createMut.isPending}
            className={INPUT_CLASS_ON_HIGHEST}
            autoComplete="off"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('upstream_accounts.add_modal.rate_multiplier')}
          </span>
          <input
            name="rate_multiplier"
            type="number"
            min={0}
            step="0.01"
            defaultValue="1.0"
            disabled={createMut.isPending}
            className={INPUT_CLASS_ON_HIGHEST}
            autoComplete="off"
          />
        </label>

        {formError !== null ? (
          <p className="text-xs font-medium text-error" role="alert">
            {formError}
          </p>
        ) : null}

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => { if (!createMut.isPending) onClose(); }}
            disabled={createMut.isPending}
            className="inline-flex items-center justify-center rounded-lg bg-surface-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('upstream_accounts.add_modal.cancel')}
          </button>
          <button
            type="submit"
            disabled={createMut.isPending}
            data-testid="accounts-create-submit"
            className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] disabled:opacity-40 disabled:pointer-events-none"
          >
            {createMut.isPending
              ? t('upstream_accounts.add_modal.creating')
              : t('upstream_accounts.add_modal.submit')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
